import { TimePeriod, type PeriodGrain } from '../types'
import { addDays, endOfISOWeek, format, getISOWeek, getISOWeekYear, parseISO, setISOWeek, startOfISOWeek } from 'date-fns'

/**
 * Generates time periods for sales forecasting
 * Returns last 6 months of historical data + full next calendar year forecast
 */
export function generatePeriods(): TimePeriod[] {
  const periods: TimePeriod[] = []
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // getMonth() returns 0-11
  
  // Generate last 6 months of historical data
  for (let i = 5; i >= 0; i--) {
    let year = currentYear
    let month = currentMonth - i
    
    // Handle year rollover
    while (month < 1) {
      month += 12
      year -= 1
    }
    
    periods.push({
      year,
      month,
      period_key: `${year}-${month.toString().padStart(2, '0')}`
    })
  }
  
  // Generate full next calendar year forecast
  const forecastYear = currentYear + 1
  for (let month = 1; month <= 12; month++) {
    periods.push({
      year: forecastYear,
      month,
      period_key: `${forecastYear}-${month.toString().padStart(2, '0')}`
    })
  }
  
  return periods
}

/** Twelve calendar months Jan–Dec for stable grid columns and totals. */
export function buildJanDecPeriodsForYear(year: number): TimePeriod[] {
  const periods: TimePeriod[] = []
  for (let month = 1; month <= 12; month++) {
    periods.push({
      year,
      month,
      period_key: `${year}-${month.toString().padStart(2, '0')}`,
    })
  }
  return periods
}

/** Fiscal year + start period for a new blank forecast (current calendar year, January start). */
export function default_blank_forecast_year_period(now: Date): {
  fiscal_year: number
  forecast_start_period: string
} {
  const y = now.getFullYear()
  return { fiscal_year: y, forecast_start_period: `${y}-01` }
}

/** Prefer `fiscal_year` on the version; else calendar year from `forecast_start_period` (YYYY-MM). */
export function calendarYearFromForecastVersion(
  fiscalYear: number | undefined | null,
  forecastStartPeriod: string | undefined | null,
  fallbackYear: number
): number {
  if (fiscalYear != null && Number.isFinite(fiscalYear)) {
    return fiscalYear
  }
  if (forecastStartPeriod && forecastStartPeriod.length >= 4) {
    const y = parseInt(forecastStartPeriod.slice(0, 4), 10)
    if (Number.isFinite(y)) {
      return y
    }
  }
  return fallbackYear
}

/** One week as a fraction of a month — how volume entered per month lands on a weekly plan. */
export const MONTHS_PER_WEEK = 12 / 52.1775

/** Monotonic sort key for mixed period encodings (`YYYY-MM`, `YYYY-Qn`, `YYYY-Pnn`, `YYYY-Www`). */
export function period_sort_key(period_key: string): number {
  const parts = period_key_to_timestamp_parts(period_key)
  return parts.ts
}

function period_key_to_timestamp_parts(period_key: string): { ts: number; grain: PeriodGrain } {
  if (/^\d{4}-\d{2}$/.test(period_key)) {
    const d = parseISO(`${period_key}-01`)
    return { ts: d.getTime(), grain: "month" }
  }
  if (/^\d{4}-Q[1-4]$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    const q = parseInt(period_key.slice(6), 10)
    const m = (q - 1) * 3 + 1
    const d = parseISO(`${y}-${String(m).padStart(2, "0")}-01`)
    return { ts: d.getTime(), grain: "quarter" }
  }
  if (/^\d{4}-P\d{2}$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    const p = parseInt(period_key.slice(6), 10)
    const day_offset = (p - 1) * 28
    const d0 = parseISO(`${y}-01-01`)
    return { ts: d0.getTime() + day_offset * 86400000, grain: "period" }
  }
  if (/^\d{4}-W\d{2}$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    const w = parseInt(period_key.slice(6), 10)
    const jan4 = parseISO(`${y}-01-04`)
    return { ts: jan4.getTime() + (w - 1) * 7 * 86400000, grain: "week" }
  }
  return { ts: new Date(period_key).getTime() || 0, grain: "month" }
}

export function parse_period_key_to_time_period(period_key: string): TimePeriod {
  if (/^\d{4}-\d{2}$/.test(period_key)) {
    const [ys, ms] = period_key.split("-")
    return {
      year: parseInt(ys, 10),
      month: parseInt(ms, 10),
      period_key,
      grain: "month",
      label: format(parseISO(`${period_key}-01`), "MMM yyyy"),
    }
  }
  if (/^\d{4}-Q[1-4]$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    const q = parseInt(period_key.slice(6), 10)
    const m = (q - 1) * 3 + 1
    return {
      year: y,
      month: m,
      period_key,
      grain: "quarter",
      label: period_key,
    }
  }
  if (/^\d{4}-P\d{2}$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    return {
      year: y,
      month: 1,
      period_key,
      grain: "period",
      label: period_key,
    }
  }
  if (/^\d{4}-W\d{2}$/.test(period_key)) {
    const y = parseInt(period_key.slice(0, 4), 10)
    const w = parseInt(period_key.slice(6), 10)
    // The ISO week's Thursday defines which calendar month the week "belongs" to, so
    // month-indexed logic (seasonality profiles, month filters) treats weeks sensibly
    // instead of seeing January 52 times.
    const thursday = addDays(startOfISOWeek(setISOWeek(parseISO(`${y}-01-04`), w)), 3)
    return {
      year: y,
      month: thursday.getMonth() + 1,
      period_key,
      grain: "week",
      label: period_key,
    }
  }
  return {
    year: parseInt(period_key.slice(0, 4), 10) || 0,
    month: 1,
    period_key,
    label: period_key,
  }
}

/**
 * An ISO-week key shifted by `delta_weeks` (negative = earlier), crossing year boundaries
 * correctly (`2027-W01` − 3 → `2026-W51`). Null for non-week keys.
 */
export function shift_week_period_key(period_key: string, delta_weeks: number): string | null {
  if (!/^\d{4}-W\d{2}$/.test(period_key)) return null
  const y = parseInt(period_key.slice(0, 4), 10)
  const w = parseInt(period_key.slice(6), 10)
  const monday = startOfISOWeek(setISOWeek(parseISO(`${y}-01-04`), w))
  const shifted = addDays(monday, delta_weeks * 7)
  return `${getISOWeekYear(shifted)}-W${String(getISOWeek(shifted)).padStart(2, "0")}`
}

export function format_period_column_label(period_key: string): string {
  return parse_period_key_to_time_period(period_key).label ?? period_key
}

/** Table/chart column title: respects `TimePeriod.label` and non-monthly `period_key` encodings. */
export function format_period_header(period: TimePeriod): string {
  const pk = period.period_key
  const rawLabel = period.label && String(period.label).trim()
  if (
    rawLabel &&
    !(/^\d{4}-W\d{2}$/.test(pk) && rawLabel === pk)
  ) {
    return rawLabel
  }
  if (/^\d{4}-\d{2}$/.test(pk)) {
    return format(parseISO(`${pk}-01`), "MMM yyyy")
  }
  if (/^\d{4}-W\d{2}$/.test(pk)) {
    const y = parseInt(pk.slice(0, 4), 10)
    const w = parseInt(pk.slice(6), 10)
    const anchor = parseISO(`${y}-01-04`)
    const d = setISOWeek(anchor, w)
    const mon = startOfISOWeek(d)
    const sun = endOfISOWeek(d)
    const sameMonth = mon.getMonth() === sun.getMonth() && mon.getFullYear() === sun.getFullYear()
    const range = sameMonth
      ? `${format(mon, "MMM d")}–${format(sun, "d, yyyy")}`
      : `${format(mon, "MMM d")}–${format(sun, "MMM d, yyyy")}`
    return `W${String(w).padStart(2, "0")} · ${range}`
  }
  if (period.grain === "month" && Number.isFinite(period.month) && period.month >= 1 && period.month <= 12) {
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ]
    return `${monthNames[period.month - 1]} ${period.year}`
  }
  return format_period_column_label(pk)
}

export function collect_period_keys_from_hierarchy(nodes: unknown[]): string[] {
  const set = new Set<string>()
  function walk(n: any): void {
    if (Array.isArray(n?.sales_data)) {
      for (const dp of n.sales_data) {
        const pk = dp.period?.period_key ?? dp.period_key
        if (typeof pk === "string" && pk.length > 0) {
          set.add(pk)
        }
      }
    }
    if (Array.isArray(n?.children)) {
      n.children.forEach(walk)
    }
  }
  for (const root of nodes) {
    walk(root as any)
  }
  return Array.from(set).sort((a, b) => period_sort_key(a) - period_sort_key(b))
}

export function periods_in_fiscal_year(keys: string[], fiscalYear: number | undefined | null): string[] {
  if (fiscalYear == null || !Number.isFinite(fiscalYear)) {
    return keys
  }
  const prefix = `${fiscalYear}-`
  const filtered = keys.filter((k) => k.startsWith(prefix))
  return filtered.length > 0 ? filtered : keys
}

/** ISO week key (`YYYY-Www`) for `date`, matching the server's week-grain buckets. */
export function iso_week_key_from_date(date: Date): string {
  const year = getISOWeekYear(date)
  const week = getISOWeek(date)
  return `${year}-W${String(week).padStart(2, "0")}`
}

export function years_in_period_keys(keys: string[]): number[] {
  const years = new Set<number>()
  for (const key of keys) {
    const year = parseInt(key.slice(0, 4), 10)
    if (Number.isFinite(year)) years.add(year)
  }
  return Array.from(years).sort((a, b) => a - b)
}

export function period_keys_span_years(keys: string[]): boolean {
  return years_in_period_keys(keys).length > 1
}

export type WeekColumnWindow =
  | { kind: "rolling" }
  | { kind: "all" }
  | { kind: "year"; year: number }

/**
 * Visible week columns for a (possibly multi-year) week-grain version.
 *
 * `rolling` starts at the current ISO week so Adam sees remaining 2026 + all of 2027 without
 * 105 columns. Falls back to every key when the from-week is past the last period in the plan.
 */
export function filter_week_column_keys(
  keys: string[],
  window: WeekColumnWindow,
  now: Date,
): string[] {
  const sorted = [...keys].sort((a, b) => period_sort_key(a) - period_sort_key(b))
  if (sorted.length === 0) return sorted
  if (window.kind === "all") return sorted
  if (window.kind === "year") {
    const prefix = `${window.year}-`
    const filtered = sorted.filter((key) => key.startsWith(prefix))
    return filtered.length > 0 ? filtered : sorted
  }
  const from_key = iso_week_key_from_date(now)
  const from = period_sort_key(from_key)
  const filtered = sorted.filter((key) => period_sort_key(key) >= from)
  return filtered.length > 0 ? filtered : sorted
}

export function compare_period_to_forecast_start(period_key: string, forecast_start: string | undefined): boolean {
  if (forecast_start == null || forecast_start === "") {
    return true
  }
  return period_sort_key(period_key) >= period_sort_key(forecast_start)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("default_blank_forecast_year_period", () => {
    it("uses calendar year from reference date", () => {
      expect(default_blank_forecast_year_period(new Date("2026-07-15"))).toEqual({
        fiscal_year: 2026,
        forecast_start_period: "2026-01",
      })
    })
  })

  describe("period_sort_key / compare_period_to_forecast_start", () => {
    it("orders quarter after month in same year", () => {
      expect(period_sort_key("2026-01")).toBeLessThan(period_sort_key("2026-Q2"))
    })

    it("compares mixed keys to forecast_start_period", () => {
      expect(compare_period_to_forecast_start("2026-Q3", "2026-04")).toBe(true)
      expect(compare_period_to_forecast_start("2026-01", "2026-Q3")).toBe(false)
    })
  })

  describe("format_period_header", () => {
    it("uses calendar month for YYYY-MM keys", () => {
      expect(
        format_period_header({
          year: 2026,
          month: 3,
          period_key: "2026-03",
          grain: "month",
        })
      ).toMatch(/Mar/)
    })

    it("does not show Jan for every ISO week column", () => {
      const h = format_period_header({
        year: 2026,
        month: 1,
        period_key: "2026-W05",
        grain: "week",
        label: "2026-W05",
      })
      expect(h).toContain("W05")
      expect(h).not.toBe("Jan 2026")
    })
  })

  describe("parse_period_key_to_time_period week months", () => {
    it("assigns the ISO week's Thursday month, not January", () => {
      expect(parse_period_key_to_time_period("2026-W05").month).toBe(1)
      expect(parse_period_key_to_time_period("2026-W10").month).toBe(3)
      expect(parse_period_key_to_time_period("2026-W53").month).toBe(12)
      expect(parse_period_key_to_time_period("2027-W27").month).toBe(7)
    })
  })

  describe("shift_week_period_key", () => {
    it("shifts within a year", () => {
      expect(shift_week_period_key("2027-W10", -3)).toBe("2027-W07")
      expect(shift_week_period_key("2027-W10", 3)).toBe("2027-W13")
    })

    it("crosses year boundaries", () => {
      expect(shift_week_period_key("2027-W01", -3)).toBe("2026-W51")
      expect(shift_week_period_key("2026-W53", 1)).toBe("2027-W01")
    })

    it("returns null for non-week keys", () => {
      expect(shift_week_period_key("2027-03", -3)).toBeNull()
    })
  })

  describe("filter_week_column_keys", () => {
    const keys = ["2026-W50", "2026-W53", "2027-W01", "2027-W52"]

    it("rolling starts at the current ISO week and keeps later years", () => {
      expect(filter_week_column_keys(keys, { kind: "rolling" }, new Date(2026, 11, 10))).toEqual([
        "2026-W50",
        "2026-W53",
        "2027-W01",
        "2027-W52",
      ])
      expect(filter_week_column_keys(keys, { kind: "rolling" }, new Date(2026, 11, 28))).toEqual([
        "2026-W53",
        "2027-W01",
        "2027-W52",
      ])
    })

    it("year window isolates one ISO year", () => {
      expect(filter_week_column_keys(keys, { kind: "year", year: 2026 }, new Date(2026, 8, 13))).toEqual([
        "2026-W50",
        "2026-W53",
      ])
    })

    it("all keeps every key in order", () => {
      expect(filter_week_column_keys(keys, { kind: "all" }, new Date(2026, 8, 13))).toEqual(keys)
    })

    it("falls back to every key when the rolling start is past the plan", () => {
      expect(filter_week_column_keys(keys, { kind: "rolling" }, new Date(2028, 0, 15))).toEqual(keys)
    })
  })

  describe("period_keys_span_years", () => {
    it("is true only when more than one calendar year is present", () => {
      expect(period_keys_span_years(["2026-W50", "2027-W01"])).toBe(true)
      expect(period_keys_span_years(["2027-W01", "2027-W52"])).toBe(false)
    })
  })
}
